import React from "react";
import classNames from "classnames";
import {
  AvatarProps,
  Avatar as DefaultAvatar,
  useChannelStateContext,
} from "stream-chat-react";
import type { StreamMessage } from "stream-chat-react";
import type { DefaultStreamChatGenerics } from "stream-chat-react";
import type { TimestampFormatterOptions } from "stream-chat-react";
import { useDispatch } from "react-redux";
import { LastMessageSeenBy } from "./LastMessageSeenBy";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { CHAT_ORIGIN_TYPE, ChatOriginType } from "constants/chatType";

type SystemMessageCustomData = {
  origin_type?: ChatOriginType;
  origin_id?: string;
};

type CustomSystemMessageProps<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = TimestampFormatterOptions & {
  message: StreamMessage<StreamChatGenerics>;
  Avatar?: React.ComponentType<AvatarProps>;
};

export const SystemMessage = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: CustomSystemMessageProps<StreamChatGenerics>
) => {
  const { Avatar = DefaultAvatar, message } = props;
  const { event, text, type, id, created_at, custom_data } = message;

  const { isAdmin } = useRoleBasedView();
  const { channel } = useChannelStateContext();
  const dispatch = useDispatch();
  const isLastMessage = channel.lastMessage().id === id;

  if (type === "system") {
    const { origin_type: originType, origin_id: originId } =
      (custom_data as SystemMessageCustomData | undefined) ?? {};

    const isOriginMessage =
      originType === CHAT_ORIGIN_TYPE.MESSAGE && Boolean(originId);

    const handleJumpToOrigin = () => {
      if (!originId) {
        return;
      }
      dispatch(setScrollToMessageId(originId));
    };

    return (
      <div>
        <div
          className={classNames(
            "str-chat__message--system t-text-center t-text-text-60 t-p-3 t-rounded-lg t-bg-[#EDECF0] t-my-2",
            {
              "t-cursor-pointer hover:t-bg-surface-lighter-grey":
                isOriginMessage,
            }
          )}
          data-testid="message-system"
          onClick={isOriginMessage ? handleJumpToOrigin : undefined}
        >
          <div className="str-chat__message--system__text">
            <div className="str-chat__message--system__line t-bg-neutral-70" />
            <p className="t-text-body-sm">{text}</p>
            <div className="str-chat__message--system__line t-bg-neutral-70" />
          </div>
        </div>
        {isAdmin && isLastMessage && (
          <LastMessageSeenBy
            messageId={id}
            messageCreatedAt={created_at}
            isSystemMessage
          />
        )}
      </div>
    );
  }

  if (event?.type === "member.removed" || event?.type === "member.added") {
    const name = event.user?.name || event.user?.id;
    const sentence = `${name} ${
      event.type === "member.added"
        ? "has joined the chat"
        : "was removed from the chat"
    }`;

    return (
      <div>
        <div
          className={classNames(
            "str-chat__event-component__channel-event t-flex t-items-center t-gap-3 t-text-text-60 t-p-3 t-rounded-lg t-bg-[#EDECF0] t-my-2"
          )}
        >
          <Avatar image={event.user?.image} name={name} user={event.user} />
          <div className="str-chat__event-component__channel-event__content t-flex t-flex-col">
            <em className="str-chat__event-component__channel-event__sentence t-text-body-sm">
              {sentence}
            </em>
          </div>
        </div>
        {isAdmin && isLastMessage && (
          <LastMessageSeenBy
            messageId={id}
            messageCreatedAt={created_at}
            isSystemMessage
          />
        )}
      </div>
    );
  }

  return null;
};
